import { createContext, forwardRef, useContext, useId, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from '../cn.js';

/**
 * Every control in the product is wrapped in a Field.
 *
 * That is not a style preference — it is how the label, the help text and the error
 * message get wired to the input with matching ids and aria-describedby without each
 * screen remembering to do it. A form built out of bare inputs is a form that fails an
 * accessibility audit in a hundred small places at once.
 */
interface FieldContextValue {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}
const FieldContext = createContext<FieldContextValue | null>(null);

export function useFieldControl() {
  const ctx = useContext(FieldContext);
  return {
    id: ctx?.id,
    'aria-describedby': ctx?.describedBy,
    'aria-invalid': ctx?.invalid || undefined,
  } as const;
}

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  help?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Small right-aligned note in the label row — "Prints on the certificate". */
  hint?: ReactNode;
  className?: string;
}

export function Field({ label, children, help, error, required, hint, className }: FieldProps) {
  const base = useId();
  const id = `${base}-control`;
  const helpId = help ? `${base}-help` : undefined;
  const errorId = error ? `${base}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
        <div className="flex items-baseline gap-2">
          <label
            htmlFor={id}
            className="[font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]"
          >
            {label}
            {required && (
              <span className="ml-1 font-bold text-[var(--pv-stop)]" title="Required">
                *
              </span>
            )}
          </label>
          {hint && (
            <span className="ml-auto [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">{hint}</span>
          )}
        </div>
        {children}
        {error ? (
          <p
            id={errorId}
            role="alert"
            className="flex items-start gap-1.5 [font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-stop)]"
          >
            <AlertCircle aria-hidden className="mt-px size-3.5 shrink-0" />
            {error}
          </p>
        ) : null}
        {help && (
          <p id={helpId} className="[font-size:var(--pv-text-xs)] leading-snug text-[var(--pv-faint)]">
            {help}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}

const controlBase =
  'w-full min-h-9 rounded-[var(--pv-radius)] border border-[var(--pv-line-strong)] bg-white px-3 py-2 ' +
  '[font-size:var(--pv-text-sm)] text-[var(--pv-ink)] placeholder:text-[var(--pv-faint)] ' +
  'transition-[border-color,box-shadow] duration-[var(--pv-duration-fast)] ' +
  'focus:border-[var(--pv-leaf-deep)] focus:shadow-[var(--pv-ring)] focus:outline-none ' +
  'aria-[invalid=true]:border-[var(--pv-stop)] aria-[invalid=true]:focus:shadow-[var(--pv-ring-danger)] ' +
  'disabled:cursor-not-allowed disabled:bg-[var(--pv-card-alt)] disabled:text-[var(--pv-muted)] ' +
  'read-only:border-dashed read-only:bg-[var(--pv-card-alt)] read-only:text-[var(--pv-ink-soft)]';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Certificate numbers, HS codes, references, API keys — anything transcribed. */
  mono?: boolean;
  /** Leading adornment, e.g. a currency or a fixed URL prefix. Not the HTML `prefix`
   *  attribute, which is a string and would collide here. */
  addon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, mono, addon, ...props },
  ref,
) {
  const field = useFieldControl();
  const input = (
    <input
      ref={ref}
      {...field}
      {...props}
      className={cn(controlBase, mono && 'pv-mono [font-size:var(--pv-text-xs)] tracking-[0.02em]', addon && 'rounded-l-none border-l-0', className)}
    />
  );
  if (!addon) return input;
  return (
    <div className="flex">
      <span className="inline-flex items-center rounded-l-[var(--pv-radius)] border border-r-0 border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] px-3 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
        {addon}
      </span>
      {input}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, ...props }, ref) {
    const field = useFieldControl();
    return (
      <textarea
        ref={ref}
        {...field}
        {...props}
        className={cn(controlBase, 'min-h-20 resize-y leading-relaxed', className)}
      />
    );
  },
);

/**
 * A native select, styled. Radix Select is used where a listbox needs search or rich
 * option rendering; for a plain list of values the native control is smaller, faster,
 * and better on a phone.
 */
export const NativeSelect = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function NativeSelect({ className, children, ...props }, ref) {
    const field = useFieldControl();
    return (
      <select
        ref={ref}
        {...field}
        {...props}
        className={cn(
          controlBase,
          "appearance-none bg-[url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath d='M1 1l4.5 4.5L10 1' stroke='%236C7869' stroke-width='1.6' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")] bg-[position:right_11px_center] bg-no-repeat pr-8",
          className,
        )}
      >
        {children}
      </select>
    );
  },
);

/**
 * The code-and-name pair — a port code, a country, a currency. It is the single most
 * common control shape in trade documentation and deserves to be one component rather
 * than two inputs in a grid, repeated forty times.
 */
export function CodeNamePair({
  codeProps,
  nameProps,
  className,
}: {
  codeProps: InputProps;
  nameProps: InputProps;
  className?: string;
}) {
  return (
    <div className={cn('grid grid-cols-[104px_minmax(0,1fr)] gap-1.5', className)}>
      <Input mono placeholder="Code" {...codeProps} />
      <Input placeholder="Name" {...nameProps} />
    </div>
  );
}
